import { useEffect, type ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { Navigate } from 'react-router';
import { isSigningOut } from '@/lib/auth-config';
import { useAnonymousRead } from '@/lib/usePages';

// Tokens live in sessionStorage, so every new tab starts unauthenticated —
// a cmd-clicked link, a bookmark, or a shared URL all round-trip through the
// Hosted UI. Carry the intended destination across that trip as OIDC `state`
// so the user lands where they aimed instead of at the welcome page.
function currentDestination(): string {
  return window.location.pathname + window.location.search;
}

// Only same-origin absolute paths. `//host` is a protocol-relative URL and
// would be an open redirect.
export function safeReturnTo(value: unknown): string {
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//')
    ? value
    : '/';
}

// The OIDC return URL.
//
// MUST WAIT for the provider to finish exchanging the code. On the first
// render `auth.user` is still undefined, so navigating immediately sent
// everyone to `/` with the recorded destination discarded — the original bug,
// reintroduced by a fix that read the destination but not the loading state.
//
// This route also must NOT sit inside `RequireAuth`: it is the thing that
// COMPLETES authentication, so gating it on being authenticated is circular.
// It is registered as a top-level route beside `/terms` for that reason.
export function AuthCallback() {
  const auth = useAuth();

  if (auth.isLoading || auth.activeNavigator) {
    return <CenteredMessage>Signing in…</CenteredMessage>;
  }

  // A failed exchange must not silently dump the user on the welcome page as
  // though nothing happened.
  if (auth.error) {
    return (
      <CenteredMessage>
        <div className="font-medium text-destructive">Sign-in could not be completed</div>
        <div className="mt-1 text-muted-foreground">{auth.error.message}</div>
        <button
          type="button"
          onClick={() => auth.signinRedirect({ state: { returnTo: '/' } })}
          className="mt-4 rounded-md border border-input px-3 py-1 text-sm hover:bg-accent"
        >
          Try again
        </button>
      </CenteredMessage>
    );
  }

  const returnTo = safeReturnTo((auth.user?.state as { returnTo?: unknown } | undefined)?.returnTo);
  return <Navigate to={returnTo} replace />;
}

// Gate any route on a valid Cognito session. While the OIDC client is
// resolving, render a small inline status. If unauthenticated, kick
// the user to the Hosted UI; once they return, the AuthProvider will
// have rehydrated `isAuthenticated` and the gated content renders.
export function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useAuth();
  // Instances that publish a read-only surface do not bounce a signed-out
  // visitor to the identity provider. `undefined` while the config request is
  // in flight — treated as "not yet known" rather than "no", so a slow
  // response cannot fire a redirect that the answer would have prevented.
  const anonymousRead = useAnonymousRead();
  const servesGuests = anonymousRead.data === true;
  const guestAnswerPending = anonymousRead.isLoading;

  useEffect(() => {
    // isSigningOut: a sign-out is mid-flight (local user cleared, /logout
    // navigation pending). Auto-redirecting here raced that navigation and
    // — with the SSO cookie still alive — signed the user straight back in.
    if (
      !auth.isLoading &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !auth.error &&
      !isSigningOut() &&
      !servesGuests &&
      !guestAnswerPending
    ) {
      void auth.signinRedirect({ state: { returnTo: currentDestination() } });
    }
  }, [auth, servesGuests, guestAnswerPending]);

  if (auth.isLoading || auth.activeNavigator) {
    return <CenteredMessage>Authenticating…</CenteredMessage>;
  }

  if (auth.error) {
    return (
      <CenteredMessage>
        <div className="font-medium text-destructive">Authentication failed</div>
        <div className="mt-1 text-muted-foreground">{auth.error.message}</div>
        <button
          type="button"
          onClick={() => auth.signinRedirect({ state: { returnTo: currentDestination() } })}
          className="mt-4 rounded-md border border-input px-3 py-1 text-sm hover:bg-accent"
        >
          Try again
        </button>
      </CenteredMessage>
    );
  }

  if (!auth.isAuthenticated) {
    if (guestAnswerPending) {
      return <CenteredMessage>Loading…</CenteredMessage>;
    }
    // Read-only guest. The server decides what that means; this only decides
    // that the page renders instead of redirecting.
    if (servesGuests) {
      return <>{children}</>;
    }
    return <CenteredMessage>Redirecting to sign in…</CenteredMessage>;
  }

  return <>{children}</>;
}

function CenteredMessage({ children }: { children: ReactNode }) {
  return (
    <div className="container mx-auto flex min-h-[60vh] max-w-md items-center justify-center text-center text-sm">
      <div>{children}</div>
    </div>
  );
}
